import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Award, Calendar, Users, GraduationCap, ArrowRight, Download,
  ShieldCheck, BookOpen, Sparkles, Briefcase, Heart, Building2, Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Reveal from "@/components/site/Reveal";
import LeadFormFields from "@/components/site/LeadFormFields";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAT_BG = "/images/hero-skandi-process.png";
const BROCHURE_BG = "/images/hero-skandi-book.png";

// ============================================================================
// A · STATS – große Zahlen mit warmem Hintergrund-Akzent
// ============================================================================
export function Stats() {
  const items = [
    { v: "2017", l: "Seit", sub: "über 1.000 Pflegekräfte qualifiziert" },
    { v: "16+", l: "Kurse", sub: "von LG1 bis Pflegedienstleitung" },
    { v: "100%", l: "Förderung", sub: "Bildungsgutschein für ausgewählte Kurse" },
    { v: "AZAV", l: "Zertifiziert", sub: "Anerkannter Bildungsträger" },
  ];
  return (
    <section className="relative bg-cream-50 py-24 md:py-36 overflow-hidden">
      {/* Hintergrund-Akzent rechts – nur dezent */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-32 -top-20 w-[640px] h-[640px] opacity-40"
        style={{
          backgroundImage: `url(${STAT_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          maskImage: 'radial-gradient(circle at center, black 30%, transparent 70%)',
          WebkitMaskImage: 'radial-gradient(circle at center, black 30%, transparent 70%)',
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex items-center gap-3 mb-3">
            <span className="h-px w-10 bg-terra" />
            <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-clay-dark">
              Zahlen, die für sich sprechen
            </span>
          </div>
        </Reveal>
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8">
          {items.map((it, i) => (
            <Reveal key={it.v} delay={i * 80}>
              <div>
                <div className="font-magazine text-5xl md:text-6xl lg:text-[5rem] text-terra font-bold leading-none">
                  {it.v}
                </div>
                <div className="mt-3 text-[12px] uppercase tracking-[0.22em] font-semibold text-clay-dark">
                  {it.l}
                </div>
                <div className="mt-1.5 text-[13px] text-sand-600 leading-snug max-w-[200px]">
                  {it.sub}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// B · TOPICS-GRID – Themen-Hub
// ============================================================================
const TOPICS = [
  {
    icon: GraduationCap, title: "Einstieg & Assistenz",
    desc: "Erste Schritte in die professionelle Pflege.",
    courses: [
      { name: "Pflegeassistent LG1 & LG2", slug: "pflegeassistent-lg1-lg2" },
      { name: "Ambulante Pflegeassistenz", slug: "ambulanter-pflegeassistent" },
      { name: "Pflegehelfer & Betreuungsassistent", slug: "pflegehilfskraft-betreuung" },
    ],
  },
  {
    icon: Heart, title: "Spezialisierung",
    desc: "Tiefes Fachwissen für besondere Pflegesituationen.",
    courses: [
      { name: "Wundexperte", slug: "wundexperte" },
      { name: "Palliativ-Care", slug: "palliativ-care" },
    ],
  },
  {
    icon: Briefcase, title: "Führung & Lehre",
    desc: "Verantwortung übernehmen – Teams führen.",
    courses: [
      { name: "Praxisanleitung", slug: "praxisanleitung" },
      { name: "Pflegedienstleitung (PDL)", slug: "pdl" },
    ],
  },
  {
    icon: Building2, title: "Pflegedienste",
    desc: "Pflichtfortbildungen & Träger-Programme.",
    courses: [
      { name: "Paket Groß (1 Jahr)", slug: "paket-gross" },
      { name: "Paket Klein", slug: "paket-klein" },
      { name: "Rezertifizierungen", slug: "rezert-praxisanleitung" },
    ],
  },
];

export function Topics() {
  return (
    <section className="bg-cream-50 py-28 md:py-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="max-w-3xl mb-14">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-terra" />
              <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">
                Themen auf einen Blick
              </span>
            </div>
            <h2 className="font-magazine font-medium text-[2.6rem] sm:text-[3.6rem] lg:text-[4.4rem] text-sand-800 leading-[1.12]">
              Finde den Bereich, <span className="text-terra">der zu dir passt.</span>
            </h2>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {TOPICS.map((t, i) => (
            <Reveal key={t.title} delay={i * 100}>
              <div className="rounded-3xl bg-white border border-cream-300 shadow-soft p-8 md:p-10 h-full flex flex-col">
                <div className="flex items-start gap-4 mb-5">
                  <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-terra/12 text-terra">
                    <t.icon className="h-5 w-5" strokeWidth={1.6} />
                  </span>
                  <div>
                    <h3 className="font-magazine text-2xl md:text-[1.75rem] text-sand-800 leading-tight">
                      {t.title}
                    </h3>
                    <p className="mt-1.5 text-sand-600 text-[14px] leading-snug">{t.desc}</p>
                  </div>
                </div>
                <ul className="mt-3 space-y-2.5 border-t border-cream-200 pt-5 flex-1">
                  {t.courses.map((c) => (
                    <li key={c.slug}>
                      <Link
                        to={`/kurse/${c.slug}`}
                        data-testid={`topic-link-${c.slug}`}
                        className="group flex items-center justify-between gap-3 text-[14px] text-sand-700 hover:text-terra transition-colors py-1"
                      >
                        <span className="font-medium">{c.name}</span>
                        <ArrowRight className="h-3.5 w-3.5 text-clay opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" strokeWidth={2} />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// C · AWARDS – Rankings & Zertifizierungen
// ============================================================================
export function Awards() {
  const items = [
    { icon: ShieldCheck, t: "AZAV", sub: "Zertifizierter Bildungsträger" },
    { icon: Award, t: "Bildungsgutschein", sub: "Ausgewählte Kurse 100 % förderfähig" },
    { icon: BookOpen, t: "Agentur für Arbeit", sub: "Anerkannter Bildungsträger" },
    { icon: Sparkles, t: "Seit 2017", sub: "Über 1.000 Absolvent:innen" },
  ];
  return (
    <section className="bg-peach-50 py-20 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex items-center gap-3 mb-3">
            <span className="h-px w-10 bg-terra" />
            <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-clay-dark">
              Zertifizierungen & Anerkennung
            </span>
          </div>
          <h2 className="font-magazine text-2xl sm:text-3xl text-sand-800 font-normal leading-tight mb-10 max-w-2xl">
            Anerkannt, wo es zählt.
          </h2>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((it, i) => (
            <Reveal key={it.t} delay={i * 80}>
              <div className="rounded-2xl bg-white border border-cream-300 shadow-soft p-6 h-full flex items-center gap-4">
                <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-rose/30 text-brand-darkred">
                  <it.icon className="h-5 w-5" strokeWidth={1.6} />
                </span>
                <div>
                  <div className="font-magazine text-lg text-sand-800 leading-tight font-semibold">{it.t}</div>
                  <div className="text-[12px] text-sand-600 mt-1 leading-snug">{it.sub}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// D · BROCHURE – Lead Magnet
// ============================================================================
export function Brochure() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", bundesland: "",
    audience: "pflegekraft", company: "", position: "",
  });
  const [state, setState] = useState({ loading: false, success: false, error: null });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setState({ loading: true, success: false, error: null });
    try {
      const res = await axios.post(`${API}/brochure/request`, {
        email: form.email,
        name: form.name,
        phone: form.phone,
        bundesland: form.bundesland,
        audience: form.audience,
        company: form.company || undefined,
        position: form.position || undefined,
        consent: true,
      });
      if (res.data?.pending_confirmation) {
        setState({ loading: false, success: true, error: null });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Bitte später erneut versuchen.";
      setState({ loading: false, success: false, error: detail });
    }
  };

  return (
    <section className="relative bg-cream-100 py-28 md:py-40 overflow-hidden">
      {/* subtle editorial grain — no washed-out image blob */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[5fr_7fr] gap-12 lg:gap-16 items-center">
          {/* LEFT · editorial headline */}
          <Reveal>
            <div>
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-10 bg-terra" />
                <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">
                  Kostenlos · PDF · 2026
                </span>
              </div>
              <h2 className="font-magazine font-medium text-[2.6rem] sm:text-[3.6rem] lg:text-[4.4rem] text-sand-800 leading-[1.08]">
                Hol dir den <br />
                <span className="text-terra">Kurs-Katalog 2026.</span>
              </h2>
              <p className="mt-6 text-sand-700 text-[15px] leading-[1.75] max-w-md">
                Alle 16 Kurse, Termine, Preise und Förderoptionen auf einen Blick.
                In Ruhe durchstöbern. Mit Kolleg:innen teilen. Beratung gibt&rsquo;s danach.
              </p>
              <ul className="mt-8 space-y-3 text-[14px] text-sand-700 max-w-md">
                <li className="flex items-center gap-2.5"><span className="inline-block h-[3px] w-4 bg-terra shrink-0" /> Sofort als PDF zum Download</li>
                <li className="flex items-center gap-2.5"><span className="inline-block h-[3px] w-4 bg-terra shrink-0" /> Alle Curricula im Detail</li>
                <li className="flex items-center gap-2.5"><span className="inline-block h-[3px] w-4 bg-terra shrink-0" /> Termine &amp; Preise 2026</li>
              </ul>
            </div>
          </Reveal>

          {/* RIGHT · form panel — cream inset, no harsh white box */}
          <Reveal delay={120}>
            <div className="bg-cream-50 p-7 md:p-10 border-t-[3px] border-terra">
              {!state.success ? (
                <form onSubmit={handleSubmit} className="grid gap-4">
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-2 block">Du bist …</label>
                    <div className="grid grid-cols-2 gap-2.5">
                      {[["pflegekraft", "Pflegekraft"], ["pflegedienst", "Pflegedienst"]].map(([v, l]) => (
                        <button
                          key={v} type="button"
                          onClick={() => setForm({ ...form, audience: v })}
                          data-testid={`brochure-audience-${v}`}
                          className={`rounded-full px-4 h-11 text-[13px] font-semibold border transition-all ${
                            form.audience === v
                              ? "border-terra bg-terra text-white"
                              : "border-cream-300 text-sand-700 hover:border-clay bg-cream-50"
                          }`}
                        >{l}</button>
                      ))}
                    </div>
                  </div>

                  <input
                    value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Vor- und Nachname *"
                    required minLength={2}
                    data-testid="brochure-name"
                    className="rounded-2xl border border-cream-300 bg-cream-50 px-5 h-13 py-3 text-[15px] outline-none focus:border-terra"
                  />
                  <input
                    required type="email" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="E-Mail-Adresse *"
                    data-testid="brochure-email"
                    className="rounded-2xl border border-cream-300 bg-cream-50 px-5 h-13 py-3 text-[15px] outline-none focus:border-terra"
                  />
                  <input
                    required type="tel" inputMode="tel" value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="Telefon (z. B. 0211 12345678) *"
                    data-testid="brochure-phone"
                    className="rounded-2xl border border-cream-300 bg-cream-50 px-5 h-13 py-3 text-[15px] outline-none focus:border-terra"
                  />
                  <select
                    required value={form.bundesland}
                    onChange={(e) => setForm({ ...form, bundesland: e.target.value })}
                    data-testid="brochure-bundesland"
                    aria-label="Bundesland"
                    className="rounded-2xl border border-cream-300 bg-cream-50 px-5 h-13 py-3 text-[15px] outline-none focus:border-terra"
                  >
                    <option value="">Bundesland * (bitte wählen)</option>
                    <option value="BW">Baden-Württemberg</option>
                    <option value="BY">Bayern</option>
                    <option value="BE">Berlin</option>
                    <option value="BB">Brandenburg</option>
                    <option value="HB">Bremen</option>
                    <option value="HH">Hamburg</option>
                    <option value="HE">Hessen</option>
                    <option value="MV">Mecklenburg-Vorpommern</option>
                    <option value="NI">Niedersachsen</option>
                    <option value="NW">Nordrhein-Westfalen</option>
                    <option value="RP">Rheinland-Pfalz</option>
                    <option value="SL">Saarland</option>
                    <option value="SN">Sachsen</option>
                    <option value="ST">Sachsen-Anhalt</option>
                    <option value="SH">Schleswig-Holstein</option>
                    <option value="TH">Thüringen</option>
                    <option value="AT">Österreich</option>
                    <option value="CH">Schweiz</option>
                    <option value="OTHER">Anderes</option>
                  </select>
                  {form.audience === "pflegedienst" && (
                    <input
                      value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                      placeholder="Pflegedienst / Einrichtung (optional)"
                      data-testid="brochure-company"
                      className="rounded-2xl border border-cream-300 bg-cream-50 px-5 h-13 py-3 text-[15px] outline-none focus:border-terra"
                    />
                  )}

                  <Button
                    type="submit"
                    disabled={state.loading}
                    data-testid="brochure-submit"
                    className="rounded-full bg-terra hover:bg-terra-dark text-white h-13 px-7 text-base font-semibold mt-1"
                  >
                    {state.loading ? "Bitte warten …" : (
                      <span className="inline-flex items-center gap-2">
                        <Mail className="h-4 w-4" strokeWidth={1.8} />
                        Katalog per E-Mail anfordern
                      </span>
                    )}
                  </Button>

                  {state.error && <p className="text-[13px] text-terra font-medium" data-testid="brochure-error">{state.error}</p>}
                  <p className="text-[11px] text-sand-500 leading-snug">
                    Mit dem Absenden stimmst du der Verarbeitung deiner Daten gemäß
                    {" "}<Link to="/datenschutz" className="underline">Datenschutzerklärung</Link> zu.
                    Wir senden dir eine Bestätigungs-Mail — erst nach Klick auf den Link
                    bekommst du den Katalog.
                  </p>
                </form>
              ) : (
                <div data-testid="brochure-success" className="text-center py-6">
                  <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-rose/30 text-brand-darkred mx-auto">
                    <Mail className="h-6 w-6" strokeWidth={1.8} />
                  </div>
                  <h3 className="mt-5 font-magazine text-2xl text-sand-800">Fast geschafft!</h3>
                  <p className="mt-3 text-sand-600 text-[14px] leading-relaxed">
                    Wir haben dir gerade eine E-Mail an <strong className="text-sand-800">{form.email}</strong> geschickt.
                    Bitte klicke darin auf den Bestätigungs-Link — danach bekommst du den Katalog sofort.
                  </p>
                  <p className="mt-3 text-[12px] text-sand-500">
                    Keine Mail erhalten? Schau bitte auch im Spam-Ordner nach.
                  </p>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
